import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, GraduationCap, Briefcase, ArrowRight } from 'lucide-react';

/**
 * NotFoundPage — proper 404 surface for unknown routes.
 *
 * SEO: Injects <meta name="robots" content="noindex,follow"> + <meta name="prerender-status-code" content="404">
 * so Googlebot drops the URL from the index instead of treating it as a Soft 404
 * (which is what happened when the catch-all returned the homepage HTML).
 *
 * UX: Offers two clear paths — Recruitment hub and Study Abroad hub.
 */
const NotFoundPage = () => {
  useEffect(() => {
    document.title = '404 — Page Not Found | Zafco HR';

    // noindex so Google drops it. Marker `data-injected-by` lets the public-layout
    // robots safety net (useRobotsReset) preserve this intentional directive
    // instead of stripping it as a stray tag.
    let robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex,follow');
    robots.setAttribute('data-injected-by', 'NotFoundPage');

    // Prerender / Netlify hint: serve as 404 status (read by Cloudflare Workers if configured)
    let prerender = document.querySelector('meta[name="prerender-status-code"]');
    if (!prerender) {
      prerender = document.createElement('meta');
      prerender.setAttribute('name', 'prerender-status-code');
      document.head.appendChild(prerender);
    }
    prerender.setAttribute('content', '404');
    prerender.setAttribute('data-injected-by', 'NotFoundPage');

    return () => {
      // Restore previous robots tag so subsequent SPA navigations re-index
      if (prevRobots) {
        robots.setAttribute('content', prevRobots);
        robots.removeAttribute('data-injected-by');
      } else if (robots && robots.parentNode) {
        robots.parentNode.removeChild(robots);
      }
      if (prerender && prerender.parentNode) {
        prerender.parentNode.removeChild(prerender);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 py-24" data-testid="not-found-page">
      <div className="max-w-3xl w-full text-center">
        <p className="text-[#C5A028] font-syne font-bold tracking-[0.3em] text-sm mb-4 uppercase">
          Error 404
        </p>
        <h1 className="text-5xl md:text-6xl font-syne font-bold text-white mb-6 leading-tight">
          This page has moved or no longer exists.
        </h1>
        <p className="text-lg text-slate-400 mb-12 max-w-xl mx-auto leading-relaxed">
          We've recently restructured our content. Choose where you'd like to go next — we'll get you to the right place.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <Link to="/" data-testid="not-found-recruitment-cta">
            <div className="group rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#C5A028]/40 transition-all p-6 text-left h-full">
              <Briefcase className="w-7 h-7 text-[#C5A028] mb-3" />
              <h2 className="text-xl font-syne font-bold text-white mb-2">Recruitment & Jobs Abroad</h2>
              <p className="text-sm text-slate-400 mb-4">Gulf & Saudi jobs, skilled migration, work visas, MEA-approved.</p>
              <span className="inline-flex items-center text-[#C5A028] text-sm font-semibold">
                Visit homepage <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>

          <Link to="/study-abroad/" data-testid="not-found-study-abroad-cta">
            <div className="group rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#C5A028]/40 transition-all p-6 text-left h-full">
              <GraduationCap className="w-7 h-7 text-[#C5A028] mb-3" />
              <h2 className="text-xl font-syne font-bold text-white mb-2">Study Abroad</h2>
              <p className="text-sm text-slate-400 mb-4">UK, Canada, Germany, Australia, Ireland, Dubai — universities, scholarships, visa guides.</p>
              <span className="inline-flex items-center text-[#C5A028] text-sm font-semibold">
                Explore destinations <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/contact" data-testid="not-found-contact-cta">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full">
              <Home className="w-4 h-4 mr-2" /> Contact Us
            </Button>
          </Link>
          <Link to="/blogs" data-testid="not-found-blog-cta">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full">
              Latest Articles
            </Button>
          </Link>
          <Link to="/calculators" data-testid="not-found-calculators-cta">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full">
              Points Calculators
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
